 
import { Directive, ElementRef, HostBinding, inject, input, OnDestroy, OnInit, Renderer2, signal } from "@angular/core";
import { AbstractControl, NG_VALIDATORS, ValidationErrors, Validator } from "@angular/forms";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { filter, skip, take } from "rxjs";

@Directive({
    selector: '[validateContactField]',
    providers: [
        { 
            provide: NG_VALIDATORS, 
            useExisting: ValidateContactFieldDirective, 
            multi: true 
        }
    ],

})
export class ValidateContactFieldDirective implements OnInit, OnDestroy, Validator {

    #elementRef = inject(ElementRef);
    #renderer2 = inject(Renderer2);

    @HostBinding('class.form-validation__field--error') hasError = false; 
    @HostBinding('class.form-validation__field--warning') hasWarning = false; 

    /**
     * Input Field -> The parent element of the tree
     */
    #inputFieldParent: HTMLElement;

    /**
     * Form Row -> The parent element of the tree
     */
    #formRowParent: HTMLElement;

    formFieldErrorType = input<'error'|'warning'>('error');
    formFieldInEditMode = input<boolean>(false);
    formFieldErrorMessage = input.required<string | Record<string, string>>();

    #displayError = signal(false);

    editMode$ = toObservable(this.formFieldInEditMode);

    constructor() {
        /**
         * Create reference to the parent nodes
         */
        this.#inputFieldParent = this.#renderer2.parentNode(this.#elementRef.nativeElement);
        this.#formRowParent = this.#renderer2.parentNode(this.#inputFieldParent);

        /**
         * Listen to the change in the status of the edit mode
         */
        this.editMode$
            .pipe(
                takeUntilDestroyed(),
                skip(1),
                filter(editMode => !editMode)
            )
            .subscribe(() => {
                this.#toggleElementVisibility();
            });
    }


    ngOnInit(): void {
        /**
         * Create reference to the parent nodes
         */
        this.#inputFieldParent = this.#renderer2.parentNode(this.#elementRef.nativeElement);
        this.#formRowParent = this.#renderer2.parentNode(this.#inputFieldParent);

        /**
         * Add the elements to the DOM
         */
        this.#insertElements();
    }

    ngOnDestroy(): void {
        this.#renderer2.destroy();
    }

    validate(control: AbstractControl): ValidationErrors | null {
        control.valueChanges.pipe(take(1))
            .subscribe(() => {
                /**
                 * Adds the correcr class to the input element
                 */
                this.#updateCorrectClass(control);

                /**
                 * Toggle Validation Elements
                 */
                this.#toggleElementVisibility(control);
            });

        return null;
    }

    #insertElements() {
        /**
         * Add the class to the input field
         */
        this.#renderer2.addClass(this.#elementRef.nativeElement, 'form-validation__field');

        /**
         * Craete the icon
         */
        const icon = this.#renderer2.createElement('i');

        /**
         * Add the required classes to the icon
         */
        this.#renderer2.setAttribute(
            icon, 
            'class', 
            `d-none fa fa-light fa-exclamation-circle form-validation__icon`
        );

        /**
         * Add the icon to the DOM
         */
        this.#renderer2.appendChild(this.#inputFieldParent, icon);

        /**
         * Create the error message container
         */
        const errorMessage = this.#renderer2.createElement('div');

        /**
         * Add the required classes to the div
         */
        this.#renderer2.setAttribute(
            errorMessage,
            'class',
            `col-8 offset-md-3 form-validation__message d-none`
        );
        
        /**
         * Add the error message to the DOM
         */
        this.#renderer2.appendChild(this.#formRowParent, errorMessage);
    }

    #updateCorrectClass(control: AbstractControl): void {
        /**
         * Check that the form field is in edit mode 
         * To not show false positives for invalid data - That hasn't been edited
         */
        if (this.formFieldInEditMode()) {
            const displayError = !!control?.errors && control?.dirty === true;
            this.#displayError.set(displayError);
        
            /**
             * If the type is error
             */
            if (this.formFieldErrorType() === 'error') {
                this.hasError = displayError;
            }

            /**
             * If the type is warning
             */
            if (this.formFieldErrorType() === 'warning') {
                this.hasWarning = displayError;
            }            
        }
    }

    #toggleElementVisibility(control?: AbstractControl): void {
        const iconElement = new ElementRef(this.#inputFieldParent).nativeElement.querySelector('.form-validation__icon');
        const errorMessageElement = new ElementRef(this.#formRowParent).nativeElement.querySelector('.form-validation__message');


        if (this.#displayError() && control) {
            const correctErrorMessage = this.#selectTheCorrectErrorMessage(control.errors);

            this.#renderer2.addClass(iconElement, `form-validation--${this.formFieldErrorType()}`);
            this.#renderer2.addClass(errorMessageElement, `form-validation--${this.formFieldErrorType()}`);

            this.#renderer2.removeClass(iconElement, 'd-none');
            this.#renderer2.removeClass(errorMessageElement, 'd-none');
            this.#renderer2.setProperty(errorMessageElement, 'innerHTML', correctErrorMessage);
        } else {

            if (!control) {
                this.#renderer2.removeClass(this.#elementRef.nativeElement, `form-validation__field--${this.formFieldErrorType()}`);
            }

            if (iconElement) {
                this.#renderer2.addClass(iconElement, 'd-none');
                this.#renderer2.removeClass(iconElement, `form-validation--${this.formFieldErrorType()}`);
            }

            if (errorMessageElement) {
                this.#renderer2.addClass(errorMessageElement, 'd-none');
                this.#renderer2.removeClass(errorMessageElement, `form-validation--${this.formFieldErrorType()}`);
                this.#renderer2.setProperty(errorMessageElement, 'innerHTML', '');
            }
        }
    }

    #selectTheCorrectErrorMessage(errors: ValidationErrors | null): string {
        const errorMessages = this.formFieldErrorMessage();

        if (typeof errorMessages === 'string') {
            return errorMessages;
        }  else {

            const validationKeys = Object.keys(errors as object);
            const hasKeys = validationKeys.length > 0;
            const firstKey = hasKeys ? validationKeys[0] : false;
            const keyInMessage = firstKey 
                ? Object.prototype.hasOwnProperty.call(errorMessages, firstKey) 
                : false;


            if (keyInMessage && firstKey ) {
                return errorMessages[firstKey];
            }
        }

        return 'Please enter a valid value';
    }

}
